import { useState, useEffect, useRef, useCallback } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import { TextStyle } from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import TextAlign from '@tiptap/extension-text-align';
import FontFamily from '@tiptap/extension-font-family';
import Link from '@tiptap/extension-link';
import { Extension } from '@tiptap/core';
import { TextSelection } from '@tiptap/pm/state';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { VariablePicker } from '@/components/ui/variable-picker';
import { PersonalizationVariable } from '@/lib/types/personalization-variable';
import { useVariableAutocomplete } from '@/lib/hooks/use-variable-autocomplete';
import { AiInstructionHighlight } from '@/lib/extensions/ai-instruction-highlight';
import {
  Type,
  Link2,
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Strikethrough,
  List,
  ListOrdered,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Eraser,
  Unlink,
  Eye,
} from 'lucide-react';

// FontSize extension for Tiptap
const FontSize = Extension.create({
  name: 'fontSize',
  addOptions() {
    return {
      types: ['textStyle'],
    };
  },
  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          fontSize: {
            default: null,
            parseHTML: (element: HTMLElement) => element.style.fontSize?.replace(/['"]+/g, ''),
            renderHTML: (attributes: Record<string, string>) => {
              if (!attributes.fontSize) {
                return {};
              }
              return {
                style: `font-size: ${attributes.fontSize}`,
              };
            },
          },
        },
      },
    ];
  },
  addCommands() {
    return {
      setFontSize: (fontSize: string) => ({ chain }: { chain: () => any }) => {
        return chain().setMark('textStyle', { fontSize }).run();
      },
      unsetFontSize: () => ({ chain }: { chain: () => any }) => {
        return chain().setMark('textStyle', { fontSize: null }).removeEmptyTextStyle().run();
      },
    };
  },
});

// Keyboard Shortcuts Extension
const KeyboardShortcuts = Extension.create({
  name: 'keyboardShortcuts',
  addKeyboardShortcuts() {
    return {
      'Mod-u': () => this.editor.commands.toggleUnderline(),
      'Mod-Shift-s': () => this.editor.commands.toggleStrike(),
      'Mod-Shift-x': () => this.editor.commands.toggleStrike(),
      'Mod-l': () => this.editor.commands.toggleBulletList(),
      'Mod-Shift-l': () => this.editor.commands.toggleOrderedList(),
      'Mod-Shift-7': () => this.editor.commands.toggleOrderedList(),
      'Mod-Shift-8': () => this.editor.commands.toggleBulletList(),
      'Tab': () => {
        if (this.editor.can().sinkListItem('listItem')) {
          return this.editor.commands.sinkListItem('listItem');
        }
        return false;
      },
      'Shift-Tab': () => {
        if (this.editor.can().liftListItem('listItem')) {
          return this.editor.commands.liftListItem('listItem');
        }
        return false;
      },
    };
  },
});

interface EmailEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  label?: string;
  className?: string;
  readOnly?: boolean;
  hasError?: boolean;
  onPreview?: () => void;
}

const FONT_FAMILIES = [
  { value: 'Arial', label: 'Arial' },
  { value: 'Helvetica', label: 'Helvetica' },
  { value: 'Times New Roman', label: 'Times New Roman' },
  { value: 'Georgia', label: 'Georgia' },
  { value: 'Verdana', label: 'Verdana' },
  { value: 'Courier New', label: 'Courier New' },
  { value: 'Comic Sans MS', label: 'Comic Sans MS' },
];

const FONT_SIZES = [
  { value: '8px', label: '8' },
  { value: '10px', label: '10' },
  { value: '12px', label: '12' },
  { value: '14px', label: '14' },
  { value: '16px', label: '16' },
  { value: '18px', label: '18' },
  { value: '20px', label: '20' },
  { value: '24px', label: '24' },
  { value: '32px', label: '32' },
  { value: '48px', label: '48' },
];

const TEXT_COLORS = [
  { value: '#000000', label: 'Black' },
  { value: '#333333', label: 'Dark Gray' },
  { value: '#666666', label: 'Gray' },
  { value: '#999999', label: 'Light Gray' },
  { value: '#FF0000', label: 'Red' },
  { value: '#00FF00', label: 'Green' },
  { value: '#0000FF', label: 'Blue' },
  { value: '#FFFF00', label: 'Yellow' },
  { value: '#FF00FF', label: 'Magenta' },
  { value: '#00FFFF', label: 'Cyan' },
];

export function EmailEditor({
  value,
  onChange,
  placeholder = "Write your email message...",
  label = "",
  className = "",
  readOnly = false,
  hasError = false,
  onPreview,
}: EmailEditorProps) {
  const [showToolbar, setShowToolbar] = useState(false);
  const [, setEditorState] = useState(0);
  const [openSelect, setOpenSelect] = useState<string | null>(null);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [linkText, setLinkText] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [clickedLink, setClickedLink] = useState<{ url: string; position: { x: number; y: number } } | null>(null);
  const [isFocused, setIsFocused] = useState(false);
  const [showVariablePicker, setShowVariablePicker] = useState(false);
  const linkMenuRef = useRef<HTMLDivElement>(null);
  const clickedLinkElementRef = useRef<HTMLElement | null>(null);
  const variableButtonRef = useRef<HTMLButtonElement>(null);

  const {
    isOpen: showAutocomplete,
    position: autocompletePosition,
    query: autocompleteQuery,
    checkForAutocomplete,
    insertVariable,
    closeAutocomplete,
  } = useVariableAutocomplete();

  // Auto-focus first input when dialog opens
  useEffect(() => {
    if (linkDialogOpen) {
      setTimeout(() => {
        const linkTextInput = document.getElementById('link-text') as HTMLInputElement;
        linkTextInput?.focus();
      }, 0);
    }
  }, [linkDialogOpen]);

  // Close link menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (clickedLink && linkMenuRef.current && !linkMenuRef.current.contains(event.target as Node)) {
        setClickedLink(null);
      }
    };

    if (clickedLink) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [clickedLink]);

  const editor = useEditor({
    immediatelyRender: false,
    editable: !readOnly,
    extensions: [
      StarterKit.configure({
        heading: false,
      }),
      TextStyle,
      Color,
      Underline,
      FontSize.configure({
        types: ['textStyle'],
      }),
      FontFamily.configure({
        types: ['textStyle'],
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-blue-600 underline',
        },
      }),
      KeyboardShortcuts,
      AiInstructionHighlight,
    ],
    content: value || '<p></p>',
    onUpdate: ({ editor: editorInstance }) => {
      if (readOnly) return;
      const html = editorInstance.getHTML();
      const normalizedHtml = html === '<p></p>' || html === '<p><br></p>' || html.trim() === '' ? '' : html;
      onChange(normalizedHtml);
      setEditorState((prev) => prev + 1);
      if (!readOnly) checkForAutocomplete(editorInstance);
    },
    onSelectionUpdate: ({ editor: editorInstance }) => {
      setEditorState((prev) => prev + 1);
      if (!readOnly) checkForAutocomplete(editorInstance);
    },
    editorProps: {
      attributes: {
        class: 'prose prose-sm sm:prose lg:prose-lg xl:prose-2xl focus:outline-none w-full px-5 pt-5',
      },
      handleClick: (view, _pos, event) => {
        const target = event.target as HTMLElement;
        const linkElement = target.closest('a');

        if (linkElement) {
          event.preventDefault();
          event.stopPropagation();
          const href = linkElement.getAttribute('href');
          if (href) {
            clickedLinkElementRef.current = linkElement;

            try {
              const linkStart = view.posAtDOM(linkElement, 0);
              if (linkStart !== null) {
                const { tr } = view.state;
                const $pos = view.state.doc.resolve(linkStart);
                tr.setSelection(TextSelection.near($pos));
                view.dispatch(tr);
              }
            } catch (e) {
              // If selection fails, continue anyway
            }

            const rect = linkElement.getBoundingClientRect();
            const editorContainer = view.dom.closest('[class*="border-control-border"]') as HTMLElement;
            if (editorContainer) {
              const containerRect = editorContainer.getBoundingClientRect();
              setClickedLink({
                url: href,
                position: {
                  x: rect.left - containerRect.left + rect.width / 2,
                  y: rect.top - containerRect.top
                }
              });
            }
          }
          return true;
        }

        return false;
      },
    },
  });


  // Sync value prop with editor content
  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      const contentToSet = value || '<p></p>';
      editor.commands.setContent(contentToSet);
    }
  }, [value, editor]);

  // Update editor editable state when readOnly prop changes
  useEffect(() => {
    if (editor) {
      editor.setEditable(!readOnly);
    }
  }, [editor, readOnly]);

  // Track focus state for outline
  useEffect(() => {
    if (!editor) return;

    const updateFocusState = () => {
      setIsFocused(editor.isFocused);
    };

    editor.on('focus', updateFocusState);
    editor.on('blur', updateFocusState);
    editor.on('selectionUpdate', updateFocusState);

    updateFocusState();

    return () => {
      editor.off('focus', updateFocusState);
      editor.off('blur', updateFocusState);
      editor.off('selectionUpdate', updateFocusState);
    };
  }, [editor]);

  // Track if editor is empty for placeholder display
  const [isEditorEmpty, setIsEditorEmpty] = useState(true);

  useEffect(() => {
    if (!editor) return;

    const checkEmpty = () => {
      setIsEditorEmpty(editor.isEmpty);
    };

    editor.on('update', checkEmpty);
    editor.on('create', checkEmpty);

    checkEmpty();

    return () => {
      editor.off('update', checkEmpty);
      editor.off('create', checkEmpty);
    };
  }, [editor]);

  const handleVariableSelect = useCallback((variable: PersonalizationVariable) => {
    if (!editor) return;
    editor.chain().focus().insertContent(variable.placeholder).run();
    setShowVariablePicker(false);
    setEditorState((prev) => prev + 1);
  }, [editor]);

  const handleAutocompleteSelect = useCallback((variable: PersonalizationVariable) => {
    insertVariable(editor, variable);
    setEditorState((prev) => prev + 1);
  }, [editor, insertVariable]);

  if (!editor) {
    return null;
  }

  const validateAndSanitizeUrl = (url: string): string | null => {
    if (!url || typeof url !== 'string') {
      return null;
    }

    url = url.trim();

    if (!/^https?:\/\//i.test(url)) {
      url = `https://${url}`;
    }

    try {
      const urlObj = new URL(url);

      if (!['http:', 'https:'].includes(urlObj.protocol)) {
        return null;
      }

      return urlObj.toString();
    } catch {
      return null;
    }
  };

  const sanitizeText = (text: string): string => {
    if (!text || typeof text !== 'string') {
      return '';
    }
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#x27;');
  };

  const handleAddLink = () => {
    if (linkUrl) {
      const sanitizedUrl = validateAndSanitizeUrl(linkUrl);
      if (!sanitizedUrl) {
        setLinkDialogOpen(false);
        setLinkText('');
        setLinkUrl('');
        return;
      }

      const { from, to } = editor.state.selection;
      const hasSelection = from !== to;

      if (hasSelection) {
        editor.chain().focus().unsetColor().setLink({ href: sanitizedUrl }).run();
      } else {
        const textToInsert = sanitizeText(linkText || sanitizedUrl);
        const escapedUrl = sanitizedUrl
          .replace(/&/g, '&amp;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&#x27;');
        editor.chain().focus().insertContent(`<a href="${escapedUrl}" class="text-blue-600 underline">${textToInsert}</a>`).run();
      }
      setEditorState((prev) => prev + 1);
    }
    setLinkDialogOpen(false);
    setLinkText('');
    setLinkUrl('');
  };

  return (
    <div className={`flex flex-col flex-1 min-h-0 ${className}`}>
      {label !== "" && (
        <Label htmlFor="email-message" className="text-sm font-medium mb-1">
          {label}
        </Label>
      )}
      <div className={`flex-1 flex flex-col mt-1 border border-control-border rounded-lg bg-input-bg shadow-lg relative min-h-0 overflow-hidden transition-[outline,box-shadow] ${
        hasError
          ? 'outline outline-1 outline-ring-error-subtle outline-offset-0'
          : ''
      } ${
        isFocused
          ? hasError
            ? 'outline-2 outline-ring-error'
            : 'outline outline-2 outline-focus-ring outline-offset-2'
          : ''
      }`}>
        <div className="flex-1 min-h-0 relative overflow-hidden rounded-b-2xl">
          {/* Gradients — in front of text (z-[8]), behind toolbars (z-10, z-20) */}
          <div className="absolute top-0 left-0 right-0 h-20 bg-gradient-to-b from-input-bg to-input-bg/0 z-[8] pointer-events-none" />
          <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-input-bg to-input-bg/0 z-[8] pointer-events-none" />
          <div className="h-full overflow-y-auto flex">
          <EditorContent
            editor={editor}
            className={`flex-1 min-h-full pt-20`}
          />
          {isEditorEmpty && (
            <div
              className="absolute left-0 right-0 px-5 text-content-subtle pointer-events-none select-none"
              style={{ fontSize: '0.93rem', top: '6.67rem' }}
            >
              {placeholder}
            </div>
          )}
          </div>
        </div>

        {showAutocomplete && autocompletePosition && (
          <VariablePicker
            onSelect={handleAutocompleteSelect}
            onClose={closeAutocomplete}
            position={autocompletePosition}
            side="bottom"
            query={autocompleteQuery}
          />
        )}

        {clickedLink && (
          <div
            ref={linkMenuRef}
            className="absolute z-50 pointer-events-auto"
            style={{
              left: `${clickedLink.position.x}px`,
              top: `${clickedLink.position.y}px`,
              transform: 'translate(-50%, 0)',
              marginTop: '24px'
            }}
          >
            <div className="bg-popover rounded-full shadow-xl border border-control-border p-2 flex gap-2">
              <Button
                variant="secondary"
                size="xs"
                className="justify-start"
                onClick={() => {
                  const safeUrl = validateAndSanitizeUrl(clickedLink.url);
                  if (safeUrl) {
                    window.open(safeUrl, '_blank', 'noopener,noreferrer');
                  }
                  setClickedLink(null);
                }}
              >
                Visit link
              </Button>
              <Button
                variant="secondary"
                size="xs"
                className="justify-start"
                onClick={() => {
                  if (clickedLinkElementRef.current && editor) {
                    try {
                      const linkStart = editor.view.posAtDOM(clickedLinkElementRef.current, 0);
                      if (linkStart !== null) {
                        const { tr } = editor.state;
                        const $pos = editor.state.doc.resolve(linkStart);
                        tr.setSelection(TextSelection.near($pos));
                        editor.view.dispatch(tr);
                      }
                    } catch (e) {
                      // Continue even if selection fails
                    }
                  }
                  editor.chain().focus().unsetLink().run();
                  setEditorState((prev) => prev + 1);
                  setClickedLink(null);
                  clickedLinkElementRef.current = null;
                }}
              >
                <Unlink className="h-3 w-3 mr-1" />
                Remove link
              </Button>
            </div>
          </div>
        )}

        {showToolbar && !readOnly && (
          <div className="absolute bottom-0 left-0 right-0 z-20 pointer-events-auto px-3 pb-3">
            <div className="flex flex-wrap items-center gap-2 bg-surface-subtle rounded-xl px-2 py-2">
              <Select
                value={editor.getAttributes('textStyle').fontFamily || 'Arial'}
                open={openSelect === 'fontFamily'}
                onOpenChange={(open) => setOpenSelect(open ? 'fontFamily' : null)}
                onValueChange={(value) => {
                  editor.chain().focus().setFontFamily(value).run();
                  setEditorState((prev) => prev + 1);
                  setOpenSelect(null);
                }}
              >
                <SelectTrigger className="h-10 w-[120px] text-xs shadow-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FONT_FAMILIES.map((font) => (
                    <SelectItem key={font.value} value={font.value} style={{ fontFamily: font.value }}>
                      {font.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select
                value={editor.getAttributes('textStyle').fontSize || '14px'}
                open={openSelect === 'fontSize'}
                onOpenChange={(open) => setOpenSelect(open ? 'fontSize' : null)}
                onValueChange={(value) => {
                  (editor.chain().focus() as any).setFontSize(value).run();
                  setEditorState((prev) => prev + 1);
                  setOpenSelect(null);
                }}
              >
                <SelectTrigger className="h-10 w-[80px] text-xs shadow-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FONT_SIZES.map((size) => (
                    <SelectItem key={size.value} value={size.value}>
                      {size.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Button
                variant="ghost"
                size="sm"
                className={`h-8 w-8 p-0  ${editor.isActive('bold') ? 'bg-surface-pressed hover:bg-surface-pressed' : ''}`}
                onClick={() => {
                  editor.chain().focus().toggleBold().run();
                  setEditorState((prev) => prev + 1);
                }}
              >
                <Bold className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className={`h-8 w-8 p-0 ${editor.isActive('italic') ? 'bg-surface-pressed hover:bg-surface-pressed' : ''}`}
                onClick={() => {
                  editor.chain().focus().toggleItalic().run();
                  setEditorState((prev) => prev + 1);
                }}
              >
                <Italic className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className={`h-8 w-8 p-0 ${editor.isActive('underline') ? 'bg-surface-pressed hover:bg-surface-pressed' : ''}`}
                onClick={() => {
                  editor.chain().focus().toggleUnderline().run();
                  setEditorState((prev) => prev + 1);
                }}
              >
                <UnderlineIcon className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className={`h-8 w-8 p-0 ${editor.isActive('strike') ? 'bg-surface-pressed hover:bg-surface-pressed' : ''}`}
                onClick={() => {
                  editor.chain().focus().toggleStrike().run();
                  setEditorState((prev) => prev + 1);
                }}
              >
                <Strikethrough className="h-4 w-4" />
              </Button>

              <Button
                variant="ghost"
                size="sm"
                className={`h-8 w-8 p-0 ${editor.isActive('bulletList') ? 'bg-surface-pressed hover:bg-surface-pressed' : ''}`}
                onClick={() => {
                  editor.chain().focus().toggleBulletList().run();
                  setEditorState((prev) => prev + 1);
                }}
              >
                <List className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className={`h-8 w-8 p-0 ${editor.isActive('orderedList') ? 'bg-surface-pressed hover:bg-surface-pressed' : ''}`}
                onClick={() => {
                  editor.chain().focus().toggleOrderedList().run();
                  setEditorState((prev) => prev + 1);
                }}
              >
                <ListOrdered className="h-4 w-4" />
              </Button>

              <Select
                value={
                  editor.isActive({ textAlign: 'center' }) ? 'center' :
                  editor.isActive({ textAlign: 'right' }) ? 'right' : 'left'
                }
                open={openSelect === 'textAlign'}
                onOpenChange={(open) => setOpenSelect(open ? 'textAlign' : null)}
                onValueChange={(value) => {
                  editor.chain().focus().setTextAlign(value as 'left' | 'center' | 'right').run();
                  setEditorState((prev) => prev + 1);
                  setOpenSelect(null);
                }}
              >
                <SelectTrigger className="h-10 w-[120px] text-xs shadow-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent side="top">
                  <SelectItem value="left">
                    <div className="flex items-center gap-2">
                      <AlignLeft className="h-4 w-4" />
                      Left
                    </div>
                  </SelectItem>
                  <SelectItem value="center">
                    <div className="flex items-center gap-2">
                      <AlignCenter className="h-4 w-4" />
                      Center
                    </div>
                  </SelectItem>
                  <SelectItem value="right">
                    <div className="flex items-center gap-2">
                      <AlignRight className="h-4 w-4" />
                      Right
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>

              <Select
                value={editor.getAttributes('textStyle').color || '#000000'}
                open={openSelect === 'textColor'}
                onOpenChange={(open) => setOpenSelect(open ? 'textColor' : null)}
                onValueChange={(value) => {
                  editor.chain().focus().setColor(value).run();
                  setEditorState((prev) => prev + 1);
                  setOpenSelect(null);
                }}
              >
                <SelectTrigger className="h-10 w-[80px] text-xs shadow-xl">
                  <div className="flex items-center gap-2">
                    <div
                      className="!w-4 !h-4 rounded-full border border-surface-swatch-ring"
                      style={{ backgroundColor: editor.getAttributes('textStyle').color || '#000000' }}
                    />
                  </div>
                </SelectTrigger>
                <SelectContent>
                  {TEXT_COLORS.map((color) => (
                    <SelectItem key={color.value} value={color.value}>
                      <div className="flex items-center gap-2">
                        <div
                          className="!w-4 !h-4 rounded-full border border-surface-swatch-ring"
                          style={{ backgroundColor: color.value }}
                        />
                        {color.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

            </div>
          </div>
        )}

        <div className={`absolute top-5 left-5 right-5 z-10 flex items-center justify-between pointer-events-none ${readOnly ? 'opacity-40' : ''}`}>
          <div className={`flex items-center gap-2 isolate ${readOnly ? 'pointer-events-none' : 'pointer-events-auto'}`}>
            <Button
              variant="secondary"
              size="sm"
              className="flex items-center gap-3 text-xs shadow-xl relative ml-2"
              onClick={() => {
                editor.chain().focus().insertContent('{}').run();
                // Move cursor between the braces
                const pos = editor.state.selection.from - 1;
                editor.commands.setTextSelection(pos);
              }}
            >
              AI Snippet
            </Button>
            <div className="relative">
              <Button
                ref={variableButtonRef}
                variant="secondary"
                size="sm"
                className="flex items-center gap-3 text-xs shadow-xl relative"
                onClick={() => setShowVariablePicker(!showVariablePicker)}
              >
                Dynamic variable
              </Button>
              {showVariablePicker && (
                <div className="absolute top-full left-0 mt-2">
                  <VariablePicker
                    onSelect={handleVariableSelect}
                    onClose={() => setShowVariablePicker(false)}
                    side="bottom"
                  />
                </div>
              )}
            </div>
            <div className="flex items-center gap-2 isolate">
              <Tooltip delayDuration={800}>
                <TooltipTrigger asChild>
                  <Button
                    variant="secondary"
                    size="sm"
                    className={`h-12 w-12 p-0 shadow-xl relative ${showToolbar ? 'bg-surface-pressed/90 !bg-none hover:bg-surface-pressed/90 border border-surface-pressed/90 hover:border-surface-pressed/90' : ''}`}
                    onClick={() => setShowToolbar(!showToolbar)}
                  >
                    <Type className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top" sideOffset={10}>
                  Text formatting
                </TooltipContent>
              </Tooltip>
              <DropdownMenu open={linkDialogOpen} onOpenChange={setLinkDialogOpen}>
                <Tooltip delayDuration={800}>
                  <TooltipTrigger asChild>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="secondary"
                        size="sm"
                        className="h-12 w-12 p-0 shadow-xl relative"
                        onClick={() => {
                          const { from, to } = editor.state.selection;
                          const selectedText = editor.state.doc.textBetween(from, to);
                          setLinkText(selectedText || '');
                          setLinkUrl('');
                        }}
                      >
                        <Link2 className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                  </TooltipTrigger>
                  <TooltipContent side="top" sideOffset={10}>
                    Add link
                  </TooltipContent>
                </Tooltip>
                <DropdownMenuContent
                  side="right"
                  align="end"
                  className="w-80 p-4 rounded-card shadow-xl"
                >
                  <div className="space-y-3">
                    <div>
                      <Label htmlFor="link-text" className="text-xs font-medium">
                        Link Text
                      </Label>
                      <Input
                        id="link-text"
                        value={linkText}
                        onChange={(e) => setLinkText(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && linkUrl) {
                            e.preventDefault();
                            handleAddLink();
                          } else if (e.key === 'Tab' && !e.shiftKey) {
                            e.preventDefault();
                            document.getElementById('link-url')?.focus();
                          }
                        }}
                        placeholder="Enter link text"
                        className="mt-1 h-10 text-sm shadow-lg"
                      />
                    </div>
                    <div>
                      <Label htmlFor="link-url" className="text-xs font-medium">
                        URL
                      </Label>
                      <Input
                        id="link-url"
                        value={linkUrl}
                        onChange={(e) => setLinkUrl(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && linkUrl) {
                            e.preventDefault();
                            handleAddLink();
                          } else if (e.key === 'Tab' && e.shiftKey) {
                            e.preventDefault();
                            document.getElementById('link-text')?.focus();
                          }
                        }}
                        placeholder="https://example.com"
                        className="mt-1 h-10 text-sm shadow-lg"
                      />
                    </div>
                    <div className="flex items-center gap-2 pt-2">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => {
                          setLinkDialogOpen(false);
                          setLinkText('');
                          setLinkUrl('');
                        }}
                        className="flex-1"
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={handleAddLink}
                        disabled={!linkUrl}
                        className="flex-1 shadow-lg"
                      >
                        Add Link
                      </Button>
                    </div>
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>
              <Tooltip delayDuration={800}>
                <TooltipTrigger asChild>
                  <Button
                    variant="secondary"
                    size="sm"
                    className="h-12 w-12 p-0 shadow-xl relative"
                    onClick={() => {
                      editor.chain().focus().clearNodes().unsetAllMarks().run();
                      setEditorState((prev) => prev + 1);
                    }}
                  >
                    <Eraser className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top" sideOffset={10}>
                  Clear formatting
                </TooltipContent>
              </Tooltip>
              {onPreview && (
                <Button
                  variant="secondary"
                  size="sm"
                  className="flex items-center gap-2 text-xs shadow-xl relative"
                  onClick={onPreview}
                >
                  <Eye className="h-4 w-4" />
                  Preview
                </Button>
              )}
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
